// src/pages/DonatePage.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Heart, CreditCard, Banknote, Bitcoin, Wallet, Landmark, Copy, Check } from 'lucide-react';
import { useAuthContext } from '../context/AuthContext';
import apiClient from '../api/apiClient';

const DonatePage = () => {
  const navigate = useNavigate();
  const { isLoggedIn, openAuthModal } = useAuthContext();
  const [donationAmount, setDonationAmount] = useState('');
  const [isDonating, setIsDonating] = useState(false);
  const [message, setMessage] = useState('');
  const [copiedField, setCopiedField] = useState(null);

  // پرداخت از طریق کیف پول
  const handleWalletDonation = async () => {
    if (!isLoggedIn) {
      openAuthModal();
      return;
    }
    if (!donationAmount || parseFloat(donationAmount) <= 0) {
      setMessage('لطفاً مبلغ معتبر وارد کنید.');
      return;
    }
    setIsDonating(true);
    setMessage('');
    try {
      const response = await apiClient.post('/wallet/donate/', {
        amount: parseFloat(donationAmount)
      });
      if (response.data.success) {
        setMessage(`✅ کمک مالی ${donationAmount} تومانی با موفقیت از کیف پول شما کسر شد.`);
        setDonationAmount('');
      } else {
        throw new Error(response.data.error || 'خطا در پرداخت');
      }
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.error || 'خطا در انجام کمک مالی. موجودی کیف پول کافی نیست؟');
    } finally {
      setIsDonating(false);
    }
  };

  // کپی متن
  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50 rtl pb-16">
      
      {/* محتوا */}
      <div className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-center mb-6">
            <Heart size={30} className="text-red-600" />
          </div>
          <h2 className="text-xs font-bold text-center text-red-800 mb-2">حمایت مالی شما، پشتوانه پیشرفت فرزانه</h2>
          <p className=" text-xs text-gray-600 text-justify leading-relaxed mb-6">
            شما می‌توانید از راه‌های زیر ما را در توسعه و بهبود اپلیکیشن یاری کنید. هر کمک شما، حتی کوچک، ارزشمند است.
          </p>

          {/* 1. کمک از طریق کیف پول */}
          <div className="border rounded-lg p-4 mb-5 bg-purple-50 border-purple-200">
            <div className="flex items-center gap-3 mb-3">
              <Wallet size={24} className="text-purple-700" />
              <h3 className="font-bold text-gray-800">کمک مالی از طریق کیف پول</h3>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-1">مبلغ به تومان</label>
                <input
                  type="number"
                  value={donationAmount}
                  onChange={(e) => setDonationAmount(e.target.value)}
                  placeholder="مثال: 50000"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-[11px]"
                />
              </div>
              <button
                onClick={handleWalletDonation}
                disabled={isDonating}
                className="bg-purple-700 text-white px-5 py-2 rounded-lg hover:bg-purple-800 transition disabled:opacity-50"
              >
                {isDonating ? 'در حال پرداخت...' : 'کمک با کیف پول'}
              </button>
            </div>
            {message && (
              <div className="mt-3 text-[11px] text-center text-purple-800 bg-white p-2 rounded">
                {message}
              </div>
            )}
          </div>

          {/* 2. کارت به کارت */}
          <div className="border rounded-lg p-4 mb-5 bg-white border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <CreditCard size={24} className="text-purple-700" />
              <h3 className="font-bold text-gray-800">پرداخت کارت به کارت</h3>
            </div>
            <div className="bg-gray-100 p-3 rounded-lg flex justify-between items-center flex-wrap gap-2">
              <span className="font-mono text-[11px] dir-ltr">6037-9974-1234-5678</span>
              <button
                onClick={() => copyToClipboard('6037997412345678', 'card')}
                className="text-purple-600 hover:text-purple-800 text-[11px] flex items-center gap-1"
              >
                {copiedField === 'card' ? <Check size={16} /> : <Copy size={16} />}
                {copiedField === 'card' ? 'کپی شد' : 'کپی شماره کارت'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">بانک ملی، به نام موسسه فرهنگی فرزانه</p>
          </div>

          {/* 3. ارز دیجیتال (USDT - TRC20) */}
          <div className="border rounded-lg p-4 mb-5 bg-white border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <Bitcoin size={24} className="text-purple-700" />
              <h3 className="font-bold text-gray-800">پرداخت از طریق ارز دیجیتال</h3>
            </div>
            <div className="bg-gray-100 p-3 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">شبکه: TRC20</p>
              <div className="flex justify-between items-center flex-wrap gap-2">
                <span className="font-mono text-xs break-all">TXYZ... (آدرس کیف پول خود را وارد کنید)</span>
                <button
                  onClick={() => copyToClipboard('TXYZ...', 'crypto')}
                  className="text-purple-600 hover:text-purple-800 text-[11px] flex items-center gap-1"
                >
                  {copiedField === 'crypto' ? <Check size={16} /> : <Copy size={16} />}
                  کپی آدرس
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">پس از انتقال، لطفاً با پشتیبانی تماس بگیرید تا واریز ثبت شود.</p>
          </div>

          {/* 4. پی پال */}
          <div className="border rounded-lg p-4 mb-5 bg-white border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <svg className="w-6 h-6 text-purple-700" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.076 21.337H2.47l2.994-14.98h4.606l-2.994 14.98zm9.016-14.98h-4.607l-2.993 14.98h4.606l2.994-14.98zm5.408 0h-4.606l-2.994 14.98h4.606l2.994-14.98z"/>
              </svg>
              <h3 className="font-bold text-gray-800">پرداخت از طریق پی‌پال</h3>
            </div>
            <div className="bg-gray-100 p-3 rounded-lg flex justify-between items-center flex-wrap gap-2">
              <span className="text-sm">donate@farzaneh.app</span>
              <button
                onClick={() => copyToClipboard('donate@farzaneh.app', 'paypal')}
                className="text-purple-600 hover:text-purple-800 text-sm flex items-center gap-1"
              >
                {copiedField === 'paypal' ? <Check size={16} /> : <Copy size={16} />}
                کپی ایمیل
              </button>
            </div>
          </div>

          {/* 5. شبا */}
          <div className="border rounded-lg p-4 mb-5 bg-white border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <Landmark size={24} className="text-purple-700" />
              <h3 className="font-bold text-gray-800">پرداخت از طریق شبا (IBAN)</h3>
            </div>
            <div className="bg-gray-100 p-3 rounded-lg flex justify-between items-center flex-wrap gap-2">
              <span className="font-mono text-sm dir-ltr">IR123456789012345678901234</span>
              <button
                onClick={() => copyToClipboard('IR123456789012345678901234', 'sheba')}
                className="text-purple-600 hover:text-purple-800 text-sm flex items-center gap-1"
              >
                {copiedField === 'sheba' ? <Check size={16} /> : <Copy size={16} />}
                کپی شبا
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">واریز به حساب موسسه فرهنگی فرزانه، بانک ملی</p>
          </div>

          <div className="bg-purple-50 rounded-lg p-4 text-center mt-2">
            <p className="text-purple-800 text-sm">با تشکر از همراهی شما</p>
            <p className="text-xs text-gray-500 mt-2">کمک‌های مالی شما، صرف توسعه نرم‌افزار، خرید سرور و تولید محتوا خواهد شد.</p>
          </div>

          <div className="mt-6">
            <Link to="/profile" className="block w-full text-center bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition">
              بازگشت به پروفایل
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonatePage;